import type { FunctionComponent } from 'react';
import classNames from 'classnames';

import { intl } from '@trello/i18n';
import { BoardIcon } from '@trello/nachos/icons/board';
import { OrganizationIcon } from '@trello/nachos/icons/organization';
import type { OptionProps, SingleValueProps } from '@trello/nachos/select';
import { Select, SelectComponents } from '@trello/nachos/select';

import type { BoardSelectorQuery } from './BoardSelectorQuery.generated';
import { useBoardSelectorQuery } from './BoardSelectorQuery.generated';
import { BoardSelectorTile } from './BoardSelectorTile';

import styles from './BoardSelector.less';

export type Member = NonNullable<BoardSelectorQuery['member']>;
export type Boards = NonNullable<Member['boards']>;
export type BoardOptionValue = Boards[number];
export type Organizations = NonNullable<Member['organizations']>;
export type Organization = Organizations[number];

export interface BoardOption {
  readonly label: string;
  readonly value: BoardOptionValue;
}

export interface GroupedBoardOptions {
  readonly label: string;
  readonly options: BoardOption[];
  readonly icon: JSX.Element;
}

export type OnBoardSelectorChangeType = (selectedBoard: BoardOption) => void;

interface BoardSelectorProps {
  readonly isDisabled?: boolean;
  readonly onChange: OnBoardSelectorChangeType;
}

export const GroupLabel: FunctionComponent<GroupedBoardOptions> = ({
  label,
  icon,
}) => (
  <div className={styles.groupLabel}>
    {icon}
    <span>{label}</span>
  </div>
);

export const Option: FunctionComponent<OptionProps<BoardOption>> = (props) => {
  const { data, isSelected } = props;

  return (
    <SelectComponents.Option
      {...props}
      className={classNames({
        [styles.boardSelectorOptionIsSelected]: isSelected,
        [styles.boardSelectorOption]: true,
      })}
    >
      <BoardSelectorTile
        backgroundColor={data.value.prefs?.backgroundColor}
        backgroundImageScaled={data.value.prefs?.backgroundImageScaled}
        name={data.value.name}
      />
    </SelectComponents.Option>
  );
};

export const SingleValue = (props: SingleValueProps<BoardOption>) => {
  const { data } = props;

  return (
    <SelectComponents.SingleValue {...props}>
      <BoardSelectorTile
        backgroundColor={data.value.prefs?.backgroundColor}
        backgroundImageScaled={data.value.prefs?.backgroundImageScaled}
        name={data.value.name}
      />
    </SelectComponents.SingleValue>
  );
};

interface OrganizationMap {
  [key: string]: GroupedBoardOptions;
}

export function responseToSelectorOptions(
  boards: Boards,
  organizations: Organizations,
) {
  const personalBoards: GroupedBoardOptions = {
    icon: <BoardIcon dangerous_className={styles.groupLabelIcon} />,
    label: intl.formatMessage({
      id: 'board section title.personal boards',
      defaultMessage: 'Personal boards',
      description: 'Title for the personal boards',
    }),
    options: [],
  };

  const orgMap: OrganizationMap = {};
  organizations.forEach(
    (org) =>
      (orgMap[org.id] = {
        icon: <OrganizationIcon dangerous_className={styles.groupLabelIcon} />,
        label: org.displayName,
        options: [],
      }),
  );

  boards.forEach((board) => {
    const { name, idOrganization } = board;

    if (idOrganization && orgMap[idOrganization]) {
      orgMap[idOrganization].options.push({
        label: name,
        value: board,
      });
    } else {
      personalBoards.options.push({
        label: name,
        value: board,
      });
    }
  });

  const orgBoards = Object.values(orgMap).filter((org) => org.options.length);
  return [personalBoards, ...orgBoards];
}

export const BoardSelector: FunctionComponent<BoardSelectorProps> = ({
  isDisabled,
  onChange,
}) => {
  const { data, error, loading } = useBoardSelectorQuery({
    waitOn: ['MemberBoards'],
  });

  if (error) {
    return <div>{JSON.stringify(error, null, 2)}</div>;
  }

  const member = data?.member;
  const boards = member?.boards || [];
  const organizations = member?.organizations || [];
  const options = responseToSelectorOptions(boards, organizations);

  return (
    <Select
      className={styles.boardSelector}
      blurInputOnSelect={true}
      components={{
        Option: Option as typeof SelectComponents.Option,
        SingleValue: SingleValue as typeof SelectComponents.SingleValue,
      }}
      // @ts-expect-error
      formatGroupLabel={GroupLabel}
      isDisabled={isDisabled}
      isLoading={loading}
      // @ts-expect-error
      onChange={onChange}
      options={options}
      placeholder={intl.formatMessage({
        id: 'templates.board_selector.select',
        defaultMessage: 'Select…',
        description: 'Placeholder for the board selector dropdown',
      })}
      isSearchable
    />
  );
};
